import { useState, useRef, useEffect, type ReactNode } from "react";

export interface SelectOption {
  value: string;
  label: ReactNode;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  icon?: string;
  width?: string;
}

export default function Select({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  disabled,
  icon,
  width,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`} style={width ? { width } : undefined}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`flex h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm text-ghasla-text-heading outline-none transition-all hover:border-[#00AAB7]/30 focus:border-[#00AAB7] focus:ring-2 focus:ring-[#00AAB7]/10 ${disabled ? "cursor-not-allowed opacity-50" : ""} ${open ? "border-[#00AAB7] ring-2 ring-[#00AAB7]/10" : ""}`}
      >
        <span className="flex items-center gap-2">
          {icon && (
            <i className={`${icon} text-sm text-ghasla-text-secondary`} />
          )}
          <span className="truncate">
            {selected?.label ?? placeholder ?? ""}
          </span>
        </span>
        <i
          className={`ri-arrow-down-s-line text-lg text-ghasla-text-secondary transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-[#E4EFEF] bg-white shadow-lg shadow-black/5">
          <div className="max-h-[240px] overflow-y-auto py-1">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex w-full cursor-pointer items-center px-3 py-2.5 text-right text-sm transition-colors hover:bg-[#F0FDFD] ${value === o.value ? "bg-[#F0FDFD] font-medium text-[#00AAB7]" : "text-ghasla-text-primary"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}